import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { RafflePricesBlock, RaffleSeriesInfo, RaffleWinnersInfo } from '../components/raffle';
import { getAcceptLanguageHeader, useI18n } from '../i18n';

const SECURE_OPTIONS = [
  { label: 'Без защиты', value: '' },
  { label: 'TLS', value: 'tls' },
  { label: 'SSL', value: 'ssl' },
];

function SMTPPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    host: '',
    port: '',
    username: '',
    password: '',
    smtp_secure: '',
  });
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    setFetching(true);
    fetch('/api/smtp-settings', {
      method: 'GET',
      headers: {
        Authorization: user?.api_token ? `Bearer ${user.api_token}` : '',
        'Accept-Language': getAcceptLanguageHeader(),
      },
    })
      .then(res => {
        if (!res.ok) throw new Error(t('Ошибка загрузки настроек'));
        return res.json();
      })
      .then(data => {
        setForm({
          host: data.host || '',
          port: data.port || '',
          username: data.username || '',
          password: data.password || '',
          smtp_secure: data.smtp_secure || '',
        });
      })
      .catch(e => {
        setSnackbar({ open: true, message: e.message || t('Ошибка загрузки настроек'), severity: 'error' });
      })
      .finally(() => setFetching(false));
  }, [user]);

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/smtp-settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: user?.api_token ? `Bearer ${user.api_token}` : '',
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify({
          host: form.host,
          port: form.port,
          username: form.username,
          password: form.password,
          smtp_secure: form.smtp_secure,
        }),
      });
      if (!res.ok) throw new Error(t('Ошибка сохранения настроек'));
      setSnackbar({ open: true, message: t('Настройки SMTP успешно сохранены!'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка сохранения'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('SMTP настройки')}</Typography>
      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 480, height: '460px' }}>
        {fetching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><span>{t('Загрузка')}</span></Box>
        ) : (
          <>
            <TextField
              label={t('SMTP сервер')}
              name="host"
              value={form.host}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('Порт')}
              name="port"
              value={form.port}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('Пользователь')}
              name="username"
              value={form.username}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('Пароль')}
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              fullWidth
              sx={{ mb: 2 }}
            />
            <TextField
              label={t('Безопасность соединения')}
              name="smtp_secure"
              value={form.smtp_secure}
              onChange={handleChange}
              select
              fullWidth
              sx={{ mb: 3 }}
            >
              {SECURE_OPTIONS.map(opt => (
                <MenuItem key={opt.value} value={opt.value}>{t(opt.label)}</MenuItem>
              ))}
            </TextField>
            <Button variant="contained" color="primary" onClick={handleSave} disabled={loading} sx={{ fontWeight: 600, minWidth: 180 }}>
              {t('Сохранить')}
            </Button>
          </>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

function TestEmailForm() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [form, setForm] = useState({
    email: '',
    subject: '',
    text: '',
  });
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSend = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/smtp-settings', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: user?.api_token ? `Bearer ${user.api_token}` : '',
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify({
          subject: form.subject,
          email: form.email,
          text: form.text,
        }),
      });
      if (!res.ok) throw new Error(t('Ошибка отправки письма'));
      setSnackbar({ open: true, message: t('Письмо успешно отправлено!'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка отправки'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('Отправка письма')}</Typography>
      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 480, height: '460px' }}>
        <TextField
          label={t('Email получателя')}
          name="email"
          value={form.email}
          onChange={handleChange}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('Тема')}
          name="subject"
          value={form.subject}
          onChange={handleChange}
          fullWidth
          sx={{ mb: 2 }}
        />
        <TextField
          label={t('Текст')}
          name="text"
          value={form.text}
          onChange={handleChange}
          fullWidth
          multiline
          minRows={7}
          sx={{ mb: 3 }}
        />
        <Button variant="contained" color="primary" onClick={handleSend} disabled={loading} sx={{ fontWeight: 600, minWidth: 180 }}>
          {t('Отправить')}
        </Button>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default function SettingsPage() {
  const { t } = useI18n();
  const [tab, setTab] = useState(0);

  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{t('Настройки')}</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label="SMTP" />
        <Tab label="API" />
        <Tab label={t('Розыгрыш')} />
      </Tabs>
      {tab === 0 && (
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'flex-start' }}>
          <SMTPPage />
          <TestEmailForm />
        </Box>
      )}
      {tab === 1 && <ApiRatesBlock />}
      {tab === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <RafflePricesBlock />
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'stretch' }}>
            <RaffleSeriesInfo />
            <RaffleWinnersInfo />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function ApiRatesBlock() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [service, setService] = useState('TMCELL');
  const [rate, setRate] = useState('');
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [gbPackages, setGbPackages] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const url = `/api/rates/latest?service=${encodeURIComponent(service)}`;
        const res = await fetch(url, { headers: { Authorization: user?.api_token ? `Bearer ${user.api_token}` : '', 'Accept-Language': getAcceptLanguageHeader() } });
        if (!res.ok) throw new Error(t('Ошибка загрузки курса'));
        const data = await res.json();
        const item = (data.items || [])[0];
        setRate(item ? String(item.rate_usd_to_tmt) : '');
      } catch (e) {
        setSnackbar({ open: true, message: e.message || t('Ошибка загрузки'), severity: 'error' });
      }
    };
    load();
  }, [service, user]);

  useEffect(() => {
    const loadGb = async () => {
      try {
        const res = await fetch(`/api/rates/latest?pattern=${encodeURIComponent('TMCELL GB%')}`, { headers: { Authorization: user?.api_token ? `Bearer ${user.api_token}` : '' } });
        if (!res.ok) return;
        const data = await res.json();
        setGbPackages((data.items || []).map(r => r.service));
      } catch {}
    };
    loadGb();
  }, [user]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/rates', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: user?.api_token ? `Bearer ${user.api_token}` : '',
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify({ service, rate_usd_to_tmt: parseFloat(rate) }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || t('Ошибка сохранения'));
      }
      setSnackbar({ open: true, message: t('Курс сохранён'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка сохранения'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>{t('Курс для сервисов')}</Typography>
      <Paper sx={{ p: 3, borderRadius: 2, maxWidth: 640 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel id="svc-label">{t('Сервис')}</InputLabel>
            <Select labelId="svc-label" label={t('Сервис')} value={service} onChange={e => setService(e.target.value)}>
              <MenuItem value="TMCELL">TMCELL</MenuItem>
              <MenuItem value="ASTU">ASTU</MenuItem>
              <MenuItem value="TurkmenTelecom">TurkmenTelecom</MenuItem>
              {gbPackages.map(pkg => (
                <MenuItem key={pkg} value={pkg}>{pkg}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={service.startsWith('TMCELL GB') && service !== 'TMCELL GB' ? t('Цена (USD)') : t('Курс USD→TMT')}
            value={rate}
            onChange={e => setRate(e.target.value)}
            type="number"
            inputProps={{ step: '0.01', min: '0' }}
            sx={{ minWidth: 220 }}
          />
          <Button variant="contained" onClick={handleSave} disabled={loading || !rate} sx={{ fontWeight: 600 }}>{t('Сохранить')}</Button>
        </Box>
        <Typography sx={{ mt: 2, color: '#666' }}>
          {t('Курс для внешних сервисов. TMCELL/ASTU/TurkmenTelecom/TMCELL GB.')}
        </Typography>
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
